import { Link } from "@inertiajs/react"
import {
  Award,
  BarChart3,
  Bookmark,
  Gift,
  GraduationCap,
  LayoutDashboard,
  MailPlus,
  Megaphone,
  MessageCircle,
  Settings,
  Truck,
  UserSearch,
  UsersRound,
} from "lucide-react"
import { useTranslation } from "react-i18next"

import { NavMain } from "@/components/nav-main"
import {
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar"
import { usePendingShipmentsCount } from "@/hooks/use-pending-shipments-count"
import { useUnreadMessagesCount } from "@/hooks/use-unread-messages-count"
import {
  agencyHomePath,
  agencySettingsProfilePath,
  badgesPath,
  brandAnalyticsMissionsPath,
  brandSettingsProfilePath,
  brandShipmentsPath,
  candidatesPath,
  crewMessagesPath,
  crewPath,
  crewSegmentsPath,
  invitationsPath,
  masterclassesPath,
  missionsPath,
  rewardsPath,
} from "@/routes"
import type { NavItem } from "@/types"

import AppLogo from "./app-logo"

/**
 * AgencySidebar — sidebar for the OKS Agency console (OKS-13).
 *
 * F1 SPLIT: cloned from `AdminSidebar`'s shape rather than extending the
 * shared `BrandSidebar`. Per team-lead-confirmed F1 split, brand and
 * agency consoles diverge fast — sharing a sidebar with role-conditional
 * branches becomes hostile within 2 tickets.
 *
 * NavUser lives in the header right slot like every other shell.
 * Icons match the AdminSidebar pattern visually:
 *   - Home          → LayoutDashboard
 *   - Invitations   → MailPlus (same as the OKS-11 inviter list)
 *
 * OKS-672 — the top-level `Team` entry was removed; agency team
 * management now lives under Agency Settings (`/agency/settings/team`,
 * reachable via the Settings sub-nav "Team" tab).
 *
 * OKS-854 — the top-level `Brands` entry was removed the same way, for
 * the same reason: it is a once-a-month screen holding a daily-menu slot.
 * Brands now lives at `/agency/settings/brands`, reachable via the
 * Settings sub-nav "Brands" tab. The sidebar drops 13 → 12 entries.
 *
 * Active-state styling comes for free from the `<NavMain>` →
 * `<SidebarMenuButton isActive>` pattern (page.url.startsWith match).
 *
 * Locked QA testid contract per OKS-13 v4 freeze + OKS-17/18 extension:
 *   - `agency-sidebar-nav-home`
 *   - `agency-sidebar-nav-recruiting` (OKS-17/18 staging-gap fix —
 *     always rendered; `/candidates` controller's
 *     `ensure_brand_context!` guard 404s for agency users without a
 *     brand context. Less FE branching, server is the floor.)
 *   - `agency-sidebar-nav-invitations`
 */
export function AgencySidebar() {
  const { t } = useTranslation()
  const unreadMessagesCount = useUnreadMessagesCount()
  const pendingShipmentsCount = usePendingShipmentsCount()

  const agencyNavItems: NavItem[] = [
    {
      title: t("agency_nav.home"),
      href: agencyHomePath(),
      icon: LayoutDashboard,
      "data-testid": "agency-sidebar-nav-home",
    },
    {
      // OKS-17/18 — Recruiting (per-brand candidate list). Always
      // rendered for agency-role users. The `/candidates` controller
      // is brand-scoped server-side via `Current.brand`; if no brand
      // context (agency user not yet drilled into a brand), the
      // controller's `ensure_brand_context!` guard 404s. Per
      // team-lead's call: less FE branching, server is the floor.
      title: t("agency_nav.recruiting"),
      href: candidatesPath(),
      icon: UserSearch,
      "data-testid": "agency-sidebar-nav-recruiting",
    },
    {
      // OKS-25 — Crew (per-brand roster). Always rendered for
      // agency-role users; `CrewMembershipsController#ensure_brand_context!`
      // 404s for agency users without a brand drill-in (same idiom
      // as Recruiting). Server is the floor — no FE branching beyond
      // role-shell mount.
      //
      // Locked testid (CONTRACT FROZEN by team-lead 2026-05-03):
      // single `sidebar-nav-crew` shared with `BrandSidebar`. The
      // entry's title is the proper noun "Crew" untranslated (UX Spec
      // §Sidebar Nav).
      title: t("nav.crew"),
      href: crewPath(),
      icon: UsersRound,
      "data-testid": "sidebar-nav-crew",
    },
    {
      // OKS-28 — Segments management nav entry mirror. Same testid
      // contract as `BrandSidebar` (`sidebar-nav-crew-segments`).
      // `CrewSegmentPolicy` permits agency_member_of_brand to CRUD;
      // server-side `ensure_brand_context!` 404s for an agency user
      // without a brand drill-in.
      title: t("crew_segments.nav.segments"),
      href: crewSegmentsPath(),
      icon: Bookmark,
      "data-testid": "sidebar-nav-crew-segments",
    },
    {
      // OKS-29 — Missions nav entry mirror. `MissionPolicy` permits
      // `agency_member_of_brand` to CRUD; server-side
      // `ensure_brand_context!` 404s for an agency user without a brand
      // drill-in (same posture as Recruiting + Segments above).
      //
      // Locked QA testid: `agency-sidebar-nav-missions` (per-shell
      // distinct from the brand sidebar's `brand-sidebar-nav-missions`,
      // unlike OKS-28's shared `sidebar-nav-crew-segments` testid).
      title: t("nav.missions"),
      href: missionsPath(),
      icon: Megaphone,
      "data-testid": "agency-sidebar-nav-missions",
    },
    {
      // OKS-984 — Shipments queue mirror. Directly after Missions per
      // UX-OQ-1: an operating surface tied to missions, not a report.
      //
      // ⚠️ Mirrored into the AGENCY shell deliberately, not just brand. An
      // agency operator assigned to the brand does the packing job as much as
      // a brand admin does — `ShipmentPolicy#index?` admits
      // `agency_member_of_brand?`, so a brand-only nav entry would hide a
      // surface the server already authorises.
      //
      // Count chip shows READY-TO-SHIP only (PRD OQ-5). Per-shell testid,
      // matching the Missions convention above.
      title: t("nav.shipments"),
      href: brandShipmentsPath(),
      icon: Truck,
      "data-testid": "agency-sidebar-nav-shipments",
      unreadCount: pendingShipmentsCount,
      unreadAriaLabel: t("shipments.badge_aria", {
        count: pendingShipmentsCount,
      }),
    },
    {
      // OKS-54 — Masterclass library nav entry mirror.
      // `MasterclassPolicy` permits `agency_member_of_brand` to CRUD;
      // server-side `ensure_brand_context!` 404s for an agency user
      // without a brand drill-in (same posture as Missions above).
      // Shared testid `sidebar-nav-masterclasses` (DOM uniqueness via
      // active shell).
      title: t("nav.training"),
      href: masterclassesPath(),
      icon: GraduationCap,
      "data-testid": "sidebar-nav-masterclasses",
    },
    {
      // OKS-59 — Rewards catalogue nav entry mirror. `RewardPolicy`
      // permits `agency_member_of_brand` to CRUD; server-side
      // `ensure_brand_context!` 404s for an agency user without a brand
      // drill-in (same posture as Missions / Masterclasses above).
      //
      // Per-shell distinct testid: `agency-sidebar-nav-rewards`
      // (vs `sidebar-nav-rewards` on the brand sidebar) — matches the
      // per-shell-testid convention used on Missions (`*nav-missions`)
      // rather than the shared-testid convention used on Masterclasses /
      // Messages. UX Spec §1 calls this out explicitly.
      title: t("nav.rewards"),
      href: rewardsPath(),
      icon: Gift,
      "data-testid": "agency-sidebar-nav-rewards",
    },
    {
      // OKS-572 — Badges catalogue nav entry mirror. Sits after Rewards,
      // mirroring the brand sidebar's recognition trio (Rewards → Badges;
      // the agency shell has no Leaderboard entry). The catalogue page,
      // `/badges` route, and `BadgePolicy#index?` shipped in OKS-538;
      // this closes the discoverability gap on the agency shell too.
      //
      // `BadgePolicy#index?` permits `agency_member_of_brand` (same read
      // gate as Rewards / Missions / Masterclasses); server-side
      // `ensure_brand_context!` 404s an agency user without a brand
      // drill-in — so the entry is unconditional here like its siblings.
      //
      // Per-shell distinct testid: `agency-sidebar-nav-badges` (vs
      // `brand-sidebar-nav-badges` on the brand sidebar) — matches the
      // per-shell-testid convention (UX Spec §2 lock).
      title: t("nav.badges"),
      href: badgesPath(),
      icon: Award,
      "data-testid": "agency-sidebar-nav-badges",
    },
    {
      // OKS-504 — Analytics nav entry mirror (EMV T6, epic OKS-479's
      // final feature). Mirrors `BrandSidebar`'s
      // `brand-sidebar-nav-analytics` EXACTLY: lands on the shared
      // `/brand/analytics/*` surface (Missions tab first; EMV is the
      // third tab per PRD OQ-B). The three analytics policies
      // (`MissionAnalyticsPolicy`, `CrewAnalyticsPolicy`,
      // `EmvAnalyticsPolicy`) all permit admin OR brand_member OR
      // `agency_member_of_brand` — so an agency drilled into an
      // assigned brand reads all three tabs; the EMV tab is the T6
      // deliverable. Server is the floor: an agency not in a brand
      // context hits the analytics controllers' `ensure_brand_context!`
      // → 404 (same idiom as Recruiting / Crew / Missions above). No
      // FE role-branching, no new i18n key (reuses `nav.analytics`),
      // read-only surface (no act-as widening — EMV policy already in
      // the OKS-313 CARVE_OUTS).
      //
      // Per-shell distinct testid: `agency-sidebar-nav-analytics`
      // (vs `brand-sidebar-nav-analytics` on the brand sidebar) —
      // matches the per-shell-testid convention used on Missions /
      // Rewards above.
      title: t("nav.analytics"),
      href: brandAnalyticsMissionsPath(),
      icon: BarChart3,
      "data-testid": "agency-sidebar-nav-analytics",
    },
    {
      // OKS-37 — Messaging nav entry mirror. `ConversationPolicy#index?`
      // permits `agency_member_of_brand` (active assignment) — same
      // gate as Missions / Recruiting / Segments above. Shared testid
      // `sidebar-nav-messages` (DOM uniqueness via active shell). Unread
      // chip surfaces the `unread_messages_count` shared prop (UX Spec
      // §4 — sidebar Messages entry shows count chip if unread > 0).
      title: t("messaging.brand.nav"),
      href: crewMessagesPath(),
      icon: MessageCircle,
      "data-testid": "sidebar-nav-messages",
      unreadCount: unreadMessagesCount,
      unreadAriaLabel: t("messaging.brand.list.unread_aria", {
        count: unreadMessagesCount,
      }),
    },
    {
      // OKS-13 inviter-side pending-list: own-agency invitations only,
      // scoped server-side by OKS-11's `InvitationPolicy.relation_scope`
      // (`agency` clause). Uses the shared `GET /invitations` route
      // (same helper AdminSidebar consumes) — scoped to the current
      // user's agency by policy. NOT `agencyInvitationsPath()` —
      // that's POST-only (`/agency/invitations`) and used by the
      // dialogs to create new invites; routing a nav GET there 404s.
      title: t("agency_nav.invitations"),
      href: invitationsPath(),
      icon: MailPlus,
      "data-testid": "agency-sidebar-nav-invitations",
    },
    {
      // OKS-21 — Mirror of `brand-sidebar-nav-settings`. BE's
      // `BrandSettingsInvitesPolicy` permits `agency_member_of_brand`
      // to read + write the invite expiration setting; this entry
      // gives agency users in a brand context the same surface.
      // The Settings page's sub-nav (Invites) lives inside the
      // page itself — no agency-specific sub-nav.
      //
      // Locked QA testid: `agency-sidebar-nav-settings`. OKS-40 update
      // points to the new Brand Settings → Profile page (was Invites).
      title: t("agency_nav.settings"),
      href: brandSettingsProfilePath(),
      icon: Settings,
      "data-testid": "agency-sidebar-nav-settings",
    },
    {
      // OKS-40 — Agency-scoped Settings entry (distinct from the
      // brand-scoped one above). Points at the agency's own profile.
      // Server-side `AgencySettingsProfilesPolicy` gates write to
      // tenant_admin; both tiers can read.
      title: t("agency_nav.agency_settings"),
      href: agencySettingsProfilePath(),
      icon: Settings,
      "data-testid": "agency-sidebar-nav-agency-settings",
    },
  ]

  return (
    <Sidebar collapsible="icon" variant="inset">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild>
              <Link href={agencyHomePath()} prefetch>
                <AppLogo />
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        <NavMain items={agencyNavItems} />
      </SidebarContent>
    </Sidebar>
  )
}
